import { useEffect, useState, type ReactNode } from 'react';
import { S, t, tList } from '../../i18n/strings';
import { useSteve } from '../../state/context';
import styles from './saas.module.css';
import { nagText, refuse, type NagState } from './signup';

/** Délai avant l'apparition, si le visiteur n'a pas encore fait défiler. */
const DELAY_MS = 9_000;

/** Distance de défilement qui la fait sortir plus tôt. */
const SCROLL_TRIGGER_PX = 400;

interface Props {
  /** Appelé quand le visiteur accepte : ouvre la modale avec le code pré-rempli. */
  readonly onAccept: (code: string) => void;
}

/**
 * La pop-up de la Steveletter, et sa cascade de relances.
 *
 * Elle sort au bout de neuf secondes, ou plus tôt si le visiteur fait défiler —
 * la mécanique exacte de toutes les pop-ups qu'on ferme sans lire. Chaque
 * tentative de fermeture en sort une nouvelle, de moins en moins argumentée.
 *
 * **La cascade finit.** Après la dernière relance, elle se ferme pour de bon et
 * ne revient pas de la visite. Une pop-up qu'on ne peut pas fermer n'est plus
 * une blague, c'est un site hostile — et c'est `signup.ts` qui en répond, pas
 * ce composant.
 */
export function SaasNewsletter({ onAccept }: Props): ReactNode {
  const { lang } = useSteve();
  const nags = tList(S.saas.newsletter.nags, lang);

  const [shown, setShown] = useState(false);
  const [step, setStep] = useState<NagState>(0);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (shown) return;

    const open = (): void => {
      setShown(true);
    };
    const onScroll = (): void => {
      if (window.scrollY > SCROLL_TRIGGER_PX) open();
    };

    const id = window.setTimeout(open, DELAY_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('scroll', onScroll);
    };
  }, [shown]);

  if (!shown || step === 'closed') return null;

  const nag = nagText(step, nags);
  const dismiss = (): void => {
    setStep((current) => refuse(current, nags.length));
  };

  // Les relances remplacent le contenu de la pop-up : c'est la même boîte qui
  // insiste, pas une pile de boîtes qui s'accumulent.
  return (
    <div className={styles.newsletter} role="dialog" aria-label={t(S.saas.newsletter.title, lang)}>
      <button
        type="button"
        className={styles.newsletterClose}
        aria-label={t(S.saas.newsletter.close, lang)}
        onClick={dismiss}
      >
        ×
      </button>

      {nag === null ? (
        <>
          <h2 className={styles.newsletterTitle}>{t(S.saas.newsletter.title, lang)}</h2>
          <p className={styles.newsletterBody}>{t(S.saas.newsletter.body, lang)}</p>
          <div className={styles.newsletterRow}>
            <input
              type="email"
              className={styles.fieldInput}
              value={email}
              placeholder={t(S.saas.newsletter.placeholder, lang)}
              aria-label={t(S.saas.newsletter.placeholder, lang)}
              autoComplete="off"
              onChange={(event) => {
                setEmail(event.currentTarget.value);
              }}
            />
          </div>
          <div className={styles.newsletterActions}>
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={() => {
                setStep('closed');
                onAccept('STEVE');
              }}
            >
              {t(S.saas.newsletter.accept, lang)}
            </button>
            <button type="button" className={styles.ctaGhost} onClick={dismiss}>
              {t(S.saas.newsletter.decline, lang)}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className={styles.newsletterNag} role="status">
            {nag}
          </p>
          <div className={styles.newsletterActions}>
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={() => {
                setStep('closed');
                onAccept('STEVE');
              }}
            >
              {t(S.saas.newsletter.accept, lang)}
            </button>
            <button type="button" className={styles.ctaGhost} onClick={dismiss}>
              {t(S.saas.newsletter.decline, lang)}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
